/**
 * SQA Dashboard — Platform SQA summary, component registry, NCR list
 * Phase 4.7 — WQ-164
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon } from '../common/Icons';
import {
  getPlatformSummary, listComponents, listNCRs, getReportCard,
  type PlatformSummary, type SQAComponent, type NCR, type ReportCard,
} from '../../services/sqaService';
import SQAReportCard from './SQAReportCard';

const IL_COLORS: Record<string, { bg: string; fg: string }> = {
  'IL-4': { bg: '#fef2f2', fg: '#dc2626' },
  'IL-3': { bg: '#fff7ed', fg: '#ea580c' },
  'IL-2': { bg: '#eff6ff', fg: '#2563eb' },
  'IL-1': { bg: '#f3f4f6', fg: '#6b7280' },
  // Underscore fallbacks (legacy)
  IL_4: { bg: '#fef2f2', fg: '#dc2626' },
  IL_3: { bg: '#fff7ed', fg: '#ea580c' },
  IL_2: { bg: '#eff6ff', fg: '#2563eb' },
  IL_1: { bg: '#f3f4f6', fg: '#6b7280' },
};

const IL_TOOLTIPS: Record<string, string> = {
  'IL-4': 'Critical — Kill switch, Laws framework, secrets management, origin verification. Requires ≥90% confidence.',
  'IL-3': 'High — Multi-tenant isolation, authentication, backup/restore. Requires ≥85% confidence.',
  'IL-2': 'Medium — Business logic, data transformation, API services.',
  'IL-1': 'Low — UI preferences, informational components, cosmetic features.',
  // Underscore fallbacks (legacy)
  'IL_4': 'Critical — Kill switch, Laws framework, secrets management, origin verification. Requires ≥90% confidence.',
  'IL_3': 'High — Multi-tenant isolation, authentication, backup/restore. Requires ≥85% confidence.',
  'IL_2': 'Medium — Business logic, data transformation, API services.',
  'IL_1': 'Low — UI preferences, informational components, cosmetic features.',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626', major: '#ea580c', minor: '#eab308', observation: '#6b7280',
};

const NCR_STATUS_COLORS: Record<string, string> = {
  open: '#dc2626', rework: '#eab308', accept: '#2563eb', defer: '#6b7280', closed: '#22c55e',
};

const SQADashboard: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [summary, setSummary] = useState<PlatformSummary | null>(null);
  const [components, setComponents] = useState<SQAComponent[]>([]);
  const [ncrs, setNCRs] = useState<NCR[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<ReportCard | null>(null);
  const [ncrStatusFilter, setNcrStatusFilter] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, n] = await Promise.all([
        getPlatformSummary(authFetch),
        listComponents(authFetch),
        listNCRs(authFetch, ncrStatusFilter ? { status: ncrStatusFilter } : undefined),
      ]);
      setSummary(s);
      setComponents(c);
      setNCRs(n);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SQA data');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading, ncrStatusFilter]);

  const viewReportCard = async (componentId: string) => {
    try {
      const rc = await getReportCard(authFetch, componentId);
      setSelectedReport(rc);
    } catch {
      setError('Failed to load report card');
    }
  };

  if (loading && !summary) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading SQA data...</div>;
  }

  if (error && !summary) {
    return <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8 }}>{error}</div>;
  }

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
        Software Quality Assurance (IEEE 730) — component integrity levels, assessment tracking, and non-conformance reports.
      </p>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div className="glass-card" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.total_components}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Components</div>
          </div>
          <div className="glass-card" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{
              fontSize: 28, fontWeight: 700,
              color: summary.overall_confidence >= 0.85 ? '#22c55e' :
                     summary.overall_confidence >= 0.7 ? '#eab308' : '#dc2626',
            }}>
              {Math.round(summary.overall_confidence * 100)}%
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Overall Confidence</div>
          </div>
          <div className="glass-card" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: summary.open_ncrs > 0 ? '#dc2626' : '#22c55e' }}>
              {summary.open_ncrs}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Open NCRs</div>
          </div>
          <div className="glass-card" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.recent_assessments}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Recent Assessments</div>
          </div>
        </div>
      )}

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Component Registry */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Component Registry</h3>
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {components.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No SQA components registered.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Name</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Level</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Mechanism</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Active</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {components.map(comp => {
                const ilStyle = IL_COLORS[comp.integrity_level] || IL_COLORS.IL_1;
                return (
                  <tr key={comp.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{comp.name}</td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <span
                        title={IL_TOOLTIPS[comp.integrity_level] || comp.integrity_level}
                        style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 10,
                          background: ilStyle.bg, color: ilStyle.fg, fontWeight: 600,
                          cursor: 'help',
                        }}>{comp.integrity_level}</span>
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-secondary)' }}>
                      {comp.mechanism || '—'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      {comp.is_active ? <span style={{ color: '#22c55e' }}>✓</span> : <span style={{ color: '#6b7280' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <button onClick={() => viewReportCard(comp.id)}
                        style={{
                          fontSize: 11, padding: '2px 10px', borderRadius: 4,
                          border: '1px solid var(--border)', cursor: 'pointer',
                          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                        }}>
                        Report Card
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Report Card Detail */}
      {selectedReport && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Report Card</h3>
            <button onClick={() => setSelectedReport(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>×</button>
          </div>
          <SQAReportCard
            variant="full"
            component={selectedReport.component}
            assessment={selectedReport.latest_assessment}
            openNCRs={selectedReport.open_ncrs}
          />
        </div>
      )}

      {/* NCR List */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Non-Conformance Reports</h3>
        <select value={ncrStatusFilter} onChange={e => setNcrStatusFilter(e.target.value)}
          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', fontSize: 13 }}>
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="rework">Rework</option>
          <option value="accept">Accept</option>
          <option value="defer">Defer</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {ncrs.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
          No NCRs found.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Title</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Severity</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Raised By</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {ncrs.map(ncr => (
                <tr key={ncr.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px' }}>{ncr.title}</td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: `${SEVERITY_COLORS[ncr.severity] || '#888'}20`,
                      color: SEVERITY_COLORS[ncr.severity] || '#888',
                    }}>{ncr.severity}</span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: `${NCR_STATUS_COLORS[ncr.status] || '#888'}20`,
                      color: NCR_STATUS_COLORS[ncr.status] || '#888',
                    }}>{ncr.status}</span>
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: 12 }}>{ncr.raised_by}</td>
                  <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {new Date(ncr.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SQADashboard;
